export default class Accordion {
    constructor(container, options = {}) {
        this.container = container;

        const allowMultiple = container.dataset.allowMultiple === 'true';
        const transitionDuration = parseInt(container.dataset.transitionDuration) || 300;
        
        this.options = {
            allowMultiple: allowMultiple,
            transitionDuration: transitionDuration,
            onToggle: null,
            ...options
        };

        this.buttons = container.querySelectorAll('[role="tab"]');
        this.init();
    }

    init() {
        this.setInitialStates();
        this.setupEventListeners();
        this.setupResizeObserver();
    }

    setInitialStates() {
        this.container.querySelectorAll('[role="tabpanel"]').forEach(panel => {
            // Measure initial height
            panel.classList.remove('hidden');
            this.updatePanelHeight(panel);

            // Reset to initial state
            if (!panel.hasAttribute('data-state')) {
                panel.classList.add('hidden');
            }

            // Set transition duration from options
            panel.style.setProperty('--tw-duration', `${this.options.transitionDuration}ms`);
        });
    }

    updatePanelHeight(panel) {
        const content = panel.children[0];
        if (content) {
            const height = content.getBoundingClientRect().height;
            panel.style.setProperty('--content-height', `${height}px`);
        }
    }

    setupResizeObserver() {
        const resizeObserver = new ResizeObserver(entries => {
            entries.forEach(entry => {
                const panel = entry.target.closest('[role="tabpanel"]');
                if (panel) {
                    this.updatePanelHeight(panel);
                }
            });
        });

        this.container.querySelectorAll('[role="tabpanel"] > *').forEach(content => {
            resizeObserver.observe(content);
        });
    }

    toggle(button, force) {
        const panelId = button.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);
        const wrapper = button.closest('.accordion-item'); // eller '.accordion-item' avhengig av din markup

        if (!panel) return;

        const isExpanded = force ?? button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', isExpanded);

        // Sett aria-expanded på wrapper også
        if (wrapper) {
            wrapper.setAttribute('aria-expanded', isExpanded);
        }

        if (isExpanded) {
            this.expandPanel(panel, button);
        } else {
            this.collapsePanel(panel, button);
        }

        if (this.options.onToggle) {
            this.options.onToggle(button, isExpanded);
        }

        if (!this.options.allowMultiple && isExpanded) {
            this.closeOtherPanels(button);
        }
    }

    expandPanel(panel, button) {
        panel.classList.remove('hidden');
        this.updatePanelHeight(panel);

        requestAnimationFrame(() => {
            panel.setAttribute('data-state', 'open');
            button.closest('.accordion-item')?.setAttribute('data-state', 'open');
        });
    }

    collapsePanel(panel, button) {
        panel.setAttribute('data-state', '');
        button.closest('.accordion-item')?.setAttribute('data-state', '');

        const handleTransitionEnd = () => {
            if (!panel.hasAttribute('data-state')) {
                panel.classList.add('hidden');
            }
        };

        panel.addEventListener('transitionend', handleTransitionEnd, { once: true });
    }

    closeOtherPanels(currentButton) {
        this.buttons.forEach(button => {
            if (button !== currentButton) {
                this.toggle(button, false);
            }
        });
    }

    handleKeyNavigation(event) {
        const target = event.currentTarget;
        const buttons = Array.from(this.buttons);
        const index = buttons.indexOf(target);

        const keyActions = {
            ArrowDown: () => buttons[index + 1] || buttons[0],
            ArrowUp: () => buttons[index - 1] || buttons[buttons.length - 1],
            Home: () => buttons[0],
            End: () => buttons[buttons.length - 1]
        };

        const action = keyActions[event.key];
        if (action) {
            event.preventDefault();
            action().focus();
        }
    }

    setupEventListeners() {
        this.buttons.forEach(button => {
            button.addEventListener('click', () => this.toggle(button));
            button.addEventListener('keydown', (e) => this.handleKeyNavigation(e));
        });
    }

    // Public methods for external control
    open(index) {
        const button = Array.from(this.buttons)[index];
        if (button) this.toggle(button, true);
    }

    close(index) {
        const button = Array.from(this.buttons)[index];
        if (button) this.toggle(button, false);
    }

    openAll() {
        if (!this.options.allowMultiple) return;
        this.buttons.forEach(button => this.toggle(button, true));
    }

    closeAll() {
        this.buttons.forEach(button => this.toggle(button, false));
    }
}